import { PageHead } from '../../components/ui/PageHead';
import { TableOfContents } from '../../components/navigation/TableOfContents';
import { ArticleBody } from '../../components/content/ArticleBody';
import styles from './PrivacyPage.module.css';

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'information-collected', label: 'Information We Collect' },
  { id: 'how-we-use', label: 'How We Use Information' },
  { id: 'sharing', label: 'Information Sharing' },
  { id: 'security', label: 'Data Security' },
  { id: 'retention', label: 'Data Retention' },
  { id: 'your-rights', label: 'Your Rights' },
  { id: 'contact', label: 'Contact Us' },
];

export function PrivacyPage() {
  return (
    <>
      <PageHead
        title="Privacy Policy"
        description="NAATS Privacy Policy - how we collect, use, and protect your personal data."
      />
      <div className={styles.inner}>
        <aside className={styles.sidebar}>
          <TableOfContents items={tocItems} />
        </aside>

        <div className={styles.content}>
          <ArticleBody>
            <h1>Privacy Policy</h1>
            <p><em>Last updated: January 2026</em></p>

            <h2 id="overview">Overview</h2>
            <p>
              NAATS ("we," "our," or "us") is committed to protecting the privacy and security of
              your personal data. This Privacy Policy explains how we collect, use, disclose, and
              safeguard information when you visit our website, interact with our platform, or
              engage with our services.
            </p>

            <h2 id="information-collected">Information We Collect</h2>
            <p>We may collect the following categories of information:</p>
            <ul>
              <li><strong>Contact Information:</strong> Name, email address, phone number, and company name when you complete our contact form or request a demo.</li>
              <li><strong>Usage Data:</strong> IP address, browser type, pages visited, time spent, and referral source through our website analytics tools.</li>
              <li><strong>Communication Records:</strong> Records of correspondence when you contact us via email or our forms.</li>
              <li><strong>Professional Information:</strong> Job title, industry, and company size when voluntarily provided.</li>
            </ul>

            <h2 id="how-we-use">How We Use Information</h2>
            <p>We use collected information for the following purposes:</p>
            <ul>
              <li>To respond to inquiries, demo requests, and consultation requests</li>
              <li>To provide and improve our platform and services</li>
              <li>To send relevant technical content and product updates (with your consent)</li>
              <li>To analyze website usage and improve user experience</li>
              <li>To comply with legal obligations and protect our legitimate interests</li>
            </ul>

            <h2 id="sharing">Information Sharing</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties. We may
              share your data in the following limited circumstances:
            </p>
            <ul>
              <li><strong>Service Providers:</strong> Trusted vendors who assist us in operating our website and CRM under strict data processing agreements.</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government authority.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, subject to confidentiality obligations.</li>
            </ul>

            <h2 id="security">Data Security</h2>
            <p>
              We implement industry-standard technical and organizational measures to protect your
              personal data against unauthorized access, alteration, disclosure, or destruction.
              These measures include encryption in transit (TLS), access controls, and regular
              security assessments. While no system is 100% secure, we are committed to maintaining
              appropriate safeguards aligned with our ISO 27001 security posture.
            </p>

            <h2 id="retention">Data Retention</h2>
            <p>
              We retain personal information for as long as necessary to fulfill the purposes
              outlined in this policy, or as required by law. Contact form submissions are
              retained for up to 3 years. Website analytics data is retained in aggregated,
              anonymized form.
            </p>

            <h2 id="your-rights">Your Rights</h2>
            <p>
              Depending on your jurisdiction, you may have the following rights regarding your
              personal data:
            </p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data, subject to legal obligations.</li>
              <li><strong>Portability:</strong> Request transfer of your data in a machine-readable format.</li>
              <li><strong>Objection:</strong> Object to processing of your data for direct marketing purposes.</li>
            </ul>

            <h2 id="contact">Contact Us</h2>
            <p>
              For privacy-related inquiries, to exercise your rights, or to report a concern,
              please contact our Data Protection team via our{' '}
              <a href="/contact">contact page</a>.
            </p>
          </ArticleBody>
        </div>
      </div>
    </>
  );
}
